import type { PanelEstudianteWire } from "@copi/shared";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiClient } from "@/lib/api/client";
import { unwrap } from "@/lib/api/unwrap";

/**
 * Hook compartido del panel del estudiante autenticado (ADR 016).
 *
 * Resuelve internamente el "expediente activo" del estudiante a través
 * del endpoint `GET /panel-estudiante/mio`. Tanto el `SidebarProcesoTesis`
 * como el `EstudianteDashboard` consumen este hook — gracias al
 * `queryKey` compartido, TanStack Query deduplica la request: un solo
 * fetch por (re)render del shell.
 *
 * Contrato del retorno:
 *  - `data: PanelEstudianteWire | null` — el panel del expediente activo,
 *    o `null` si el estudiante todavía no tiene expediente creado.
 *  - `isPending` / `isError` / `error` / `refetch` — pasthrough de
 *    TanStack Query, para que los consumidores manejen los estados de
 *    forma uniforme.
 *
 * Invalidación: las mutaciones que cambian el estado del expediente
 * (presentar solicitud, emitir conformidad, etc.) deben invalidar
 * `["panel-estudiante", "mio"]` para refrescar el panel sin perder
 * cache de otras vistas.
 */
const QUERY_KEY = ["panel-estudiante", "mio"] as const;
const STALE_MS = 30_000;

export function usePanelEstudianteActivo(opciones?: {
  enabled?: boolean;
}): UseQueryResult<PanelEstudianteWire | null> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => apiClient.get("/panel-estudiante/mio").then(unwrap<PanelEstudianteWire | null>),
    staleTime: STALE_MS,
    enabled: opciones?.enabled ?? true,
  });
}

/** Re-exporto la queryKey para que los consumidores invaliden de forma DRY. */
export const panelEstudianteMioQueryKey = QUERY_KEY;
